import { Component, OnInit, OnChanges, Input, SimpleChanges, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Projection, PlanningEntity } from '../../../shared/models';
import { PlanningDataService, PartyRolesService, PlanningApiService, PlanningFormService } from '../../../shared/services';

@Component({
  selector: 'financial-details',
  templateUrl: './financial-details.component.html',
  styleUrls: ['./financial-details.component.css']
})
export class FinancialDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projection: Projection;

  minFractionDigits = 2;

  currentProjection: Projection;
  primaryEntity: PlanningEntity;
  tradingPartner: PlanningEntity;
  formSubscription: Subscription;
  selectedPartyTitle;
  partyTitles;

  // Form
  financialDataForm = new FormGroup({});

  constructor(
    private _partyRolesService: PartyRolesService,
    private _planningApiService: PlanningApiService,
    private _planningDataService: PlanningDataService,
    private _planningFormService: PlanningFormService
  ) { }

  ngOnInit() {
    this.currentProjection = this.projection;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projection.firstChange || this.currentProjection.projectionId === changes.projection.currentValue.projectionId) {
      this.currentProjection = changes.projection.currentValue;

      if (this.currentProjection.transactionType) {
        this.getAllPartyRoles();
      }
      this.getBothEntites();

      this.financialDataForm = new FormGroup({
        primaryEntitySales: new FormControl(this.primaryEntity.financials.sales),
        tradingPartnerSales: new FormControl(this.tradingPartner.financials.sales || null),
        primaryEntityCogs: new FormControl(this.primaryEntity.financials.cogs),
        tradingPartnerCogs: new FormControl(this.tradingPartner.financials.cogs || null),
        primaryEntityGross: new FormControl({
          value: this.primaryEntity.financials.sales - this.primaryEntity.financials.grossProfit || null, disabled: true
        }),
        tradingPartnerGross: new FormControl({ value: this.tradingPartner.financials.grossProfit || null, disabled: true }),
        primaryEntityExpense: new FormControl(this.primaryEntity.financials.operatingExpense || null),
        tradingPartnerExpense: new FormControl(this.tradingPartner.financials.operatingExpense || null),
        primaryEntityOperatingProfit: new FormControl({ value: this.primaryEntity.financials.operatingProfit || null, disabled: true }),
        tradingPartnerOperatingProfit: new FormControl({ value: this.tradingPartner.financials.operatingProfit || null, disabled: true })
      });
      if (this.currentProjection.testedPartyCharacterization && this.currentProjection.testedPartyCharacterization.primaryFunctionId === 2) {
        this.financialDataForm.addControl(
          'primaryEntityOperatingMargin', new FormControl({ value: this.primaryEntity.financials.operatingMargin || null, disabled: true })
        );
        this.financialDataForm.addControl(
          'tradingPartnerOperatingMargin', new FormControl({ value: this.tradingPartner.financials.operatingMargin || null, disabled: true })
        );
      } else if (this.currentProjection.testedPartyCharacterization && this.currentProjection.testedPartyCharacterization.primaryFunctionId === 3) {
        this.financialDataForm.addControl(
          'primaryEntityNetCostPlus', new FormControl({ value: this.primaryEntity.financials.netCostPlus || null, disabled: true })
        );
        this.financialDataForm.addControl(
          'tradingPartnerNetCostPlus', new FormControl({ value: this.tradingPartner.financials.netCostPlus || null, disabled: true })
        );
      }
      this.setValidators();

      // this subscription dies and is recreated with each new form created
      // NO LEAK HERE
      this.financialDataForm.valueChanges.subscribe((data) => {
        this.handleFormChange(data);
      });

      this.financialDataForm.statusChanges.subscribe((form) => {
        if (form === 'VALID' && this.primaryEntity.transactionTypeTitle) {
          this._planningFormService.setFormStatus('financialDetialsForm', true);
        } else {
          this._planningFormService.setFormStatus('financialDetialsForm', false);
        }

        this.updateFormValuesInProjection();
      });
    } else {
      this.currentProjection = changes.projection.currentValue;
    }
  }

  setValidators() {
    const controls = this.financialDataForm.controls;
    const testedPartyString = this.primaryEntity.isTestedParty ? 'primary' : 'trading';

    for (const control in controls) {
      if (control.includes(testedPartyString)) {
        controls[control].setValidators([Validators.required, Validators.min(0)]);
      }
    }
  }

  shouldBeRequired(entityString: string) {
    return (this.tradingPartner.isPrimaryEntity && entityString === 'trading') || (this.primaryEntity.isPrimaryEntity && entityString === 'primary');
  }

  ngOnDestroy() {
    this.saveForm();
  }

  saveForm() {
    this._planningApiService.saveScenario(this._planningDataService.getCurrentScenarioValue()).subscribe();
  }

  selectPartyType(index: number) {
    let arr = [...this.partyTitles];
    if (index === 1) {
      this.primaryEntity.transactionTypeTitle = arr.pop();
      this.tradingPartner.transactionTypeTitle = arr.pop();
    } else {
      this.tradingPartner.transactionTypeTitle = arr.pop();
      this.primaryEntity.transactionTypeTitle = arr.pop();
    }

    if (this.financialDataForm.status === 'VALID') {
      this._planningFormService.setFormStatus('financialDetialsForm', true);
    } else {
      this._planningFormService.setFormStatus('financialDetialsForm', false);
    }
    
    this.updateFormValuesInProjection();
    this.saveForm();
  }

  handleFormChange(data) {
    for (let field in data) {
      if (typeof (data[field]) === 'string') {
        const parsedString = data[field].replace(/,/g, '');
        let newValue = parseInt(parsedString, 10);

        if (isNaN(newValue)) {
          newValue = null;
        }
        this.financialDataForm.patchValue({ [field]: newValue }, { emitEvent: false });
      }
    }

    const bothEntites = {
      primaryEntity: this.primaryEntity,
      tradingPartner: this.tradingPartner
    };

    this._planningDataService.updateEntityFinancials(this.currentProjection.projectionId, bothEntites);

    this.updateForm();
  }

  getBothEntites() {
    if (this.currentProjection.dstEntity && this.currentProjection.dstEntity.isPrimaryEntity) {
      this.primaryEntity = this.currentProjection.dstEntity;
      this.tradingPartner = this.currentProjection.srcEntity;
    } else if (this.currentProjection.srcEntity && this.currentProjection.srcEntity.isPrimaryEntity) {
      this.primaryEntity = this.currentProjection.srcEntity;
      this.tradingPartner = this.currentProjection.dstEntity;
    }
  }

  switchPartyTypes() {
    let currentPrimary = this.primaryEntity.transactionTypeTitle;

    this.primaryEntity.transactionTypeTitle = this.tradingPartner.transactionTypeTitle;
    this.tradingPartner.transactionTypeTitle = currentPrimary;
    this.saveForm();
  }

  updateForm() {
    const currentFormValues = this.financialDataForm.value;
    const primaryEntitySales = Number(this.financialDataForm.get('primaryEntitySales').value);
    const primaryEntityCogs = Number(this.financialDataForm.get('primaryEntityCogs').value);
    const primaryEntityExpense = Number(this.financialDataForm.get('primaryEntityExpense').value) || null;

    const tradingPartnerSales = Number(this.financialDataForm.get('tradingPartnerSales').value);
    const tradingPartnerCogs = Number(this.financialDataForm.get('tradingPartnerCogs').value);
    const tradingPartnerExpense = this.financialDataForm.get('tradingPartnerExpense').value || null;

    this.primaryEntity.financials = Object.assign(this.primaryEntity.financials, {
      'operatingExpense': currentFormValues.primaryEntityExpense,
      'cogs': currentFormValues.primaryEntityCogs,
      'sales': currentFormValues.primaryEntitySales,
      'grossProfit': primaryEntitySales - primaryEntityCogs,
      'operatingProfit': (primaryEntitySales - primaryEntityCogs) - primaryEntityExpense,
      'netCostPlus': ((primaryEntitySales - primaryEntityCogs) - primaryEntityExpense) / (primaryEntityCogs + primaryEntityExpense) || null,
      'operatingMargin': ((primaryEntitySales - primaryEntityCogs) - primaryEntityExpense) / primaryEntitySales || null
    });
    this.tradingPartner.financials = Object.assign(this.tradingPartner.financials, {
      'operatingExpense': currentFormValues.tradingPartnerExpense,
      'cogs': currentFormValues.tradingPartnerCogs,
      'sales': currentFormValues.tradingPartnerSales,
      'grossProfit': tradingPartnerSales - tradingPartnerCogs,
      'operatingProfit': ((tradingPartnerSales - tradingPartnerCogs) - tradingPartnerExpense),
      'netCostPlus': ((tradingPartnerSales - tradingPartnerCogs) - tradingPartnerExpense) / (tradingPartnerCogs + tradingPartnerExpense) || null,
      'operatingMargin': (((tradingPartnerSales - tradingPartnerCogs) - tradingPartnerExpense) / tradingPartnerSales) || null
    });

    const bothEntites = {
      primaryEntity: this.primaryEntity,
      tradingPartner: this.tradingPartner
    };

    this._planningDataService.updateEntityFinancials(this.currentProjection.projectionId, bothEntites);
  }

  updateFormValuesInProjection() {
    const currentFormStatus = this._planningFormService.getCurrentFormStatus();
    this._planningDataService.updateFormValues(currentFormStatus, this.currentProjection.projectionId);
  }

  private getAllPartyRoles(): void {
    this._partyRolesService.list().subscribe(({ data }) => {
      const partyRoles = data;
      let selectedPartyRole;

      if (this.currentProjection.transactionType) {
        partyRoles.forEach((role) => {
          if (role.partyRoleId === this.currentProjection.transactionType.partyRoleId) {
            selectedPartyRole = role;
          }
        });
      }
      this.partyTitles = selectedPartyRole.name.split('/');
      this.selectedPartyTitle = {
        'firstWord': this.partyTitles[0],
        'secondWord': this.partyTitles[1]
      };
    });
  }

}
